import { useCallback } from 'react';
import { nanoid } from 'nanoid';

import { SettingsGroup } from 'approot/activity-builder/settings/settings.style';
import {
  ACTIVITIES_RESPONSE_VARIANT_IMAGE,
  ACTIVITIES_RESPONSE_VARIANT_TEXT,
  ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE,
} from 'global/constants';
import { SettingProps } from '../interactive-response-categorise.constants';
import { MAX_NUMBER_ITEMS } from './interactive-response-categorise-settings.constants';
import { useInteractiveResponseCategoriseUpdate } from './interactive-response-categorise.hooks';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import { getVariant } from '../../shared/interactive-response-shared.utils';
import InteractiveResponseCategoriseItemsText from './interactive-response-categorise-items-text';
import InteractiveResponseCategoriseItemsTextImage from './interactive-response-categorise-items-text-image';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import { DIRECTION_LEFT } from 'webclient.constants';
import { FIT } from 'approot/activity-builder/components/image/image-crop.constants';
import { COLOUR_WHITE } from '../../interactive-response-shared.constants';

const InteractiveResponseCategoriseItems = (props: SettingProps) => {
  const { properties } = props;

  const variant = getVariant({
    variant: properties.interactiveResponseCategorise.variant,
  });

  // if any child does not have a variant, set the variant to the parent variant
  const items = properties.interactiveResponseCategorise.items.map(item => ({
    ...item,
    variant: item.variant || variant,
  }));

  const { handleUpdate } = useInteractiveResponseCategoriseUpdate(props);

  const isAtMaxItems = items.length >= MAX_NUMBER_ITEMS;

  const createNewItem = useCallback(
    (itemVariant: string) => {
      const firstCategory =
        properties.interactiveResponseCategorise.categories[0];
      handleUpdate(
        {
          ...properties,
          interactiveResponseCategorise: {
            ...properties.interactiveResponseCategorise,
            items: [
              ...items,
              {
                itemId: nanoid(),
                answerLabel: '',
                questionLabel: '',
                categoryId: firstCategory?.id ?? null,
                text: null,
                richtext: null,
                baseColour: COLOUR_WHITE.base,
                audio: null,
                sort: getReorderSort(items, items.length, DIRECTION_LEFT),
                cropData: null,
                sourceUrl: null,
                imageMode: FIT,
                imageUrls: [],
                variant: itemVariant,
                image: null,
                __typename: 'CategoriseResponseItem',
              },
            ],
          },
        },
        {
          isSaveImmediate: true,
        }
      );
    },
    [properties, items, handleUpdate]
  );

  const handleAddImageOnly = useCallback(() => {
    createNewItem(ACTIVITIES_RESPONSE_VARIANT_IMAGE);
  }, [createNewItem]);

  const handleAddTextOnly = useCallback(() => {
    createNewItem(ACTIVITIES_RESPONSE_VARIANT_TEXT);
  }, [createNewItem]);

  const handleAddImageAndText = useCallback(() => {
    createNewItem(ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE);
  }, [createNewItem]);

  return (
    <>
      <SettingsGroup>
        <BoxList data-selector="items-list">
          {items.map((item, index) => {
            if (item.variant === ACTIVITIES_RESPONSE_VARIANT_TEXT) {
              return (
                <InteractiveResponseCategoriseItemsText
                  key={item.itemId}
                  {...props}
                  item={item}
                  data-selector={`categorise-item-${index}`}
                />
              );
            } else {
              return (
                <InteractiveResponseCategoriseItemsTextImage
                  key={item.itemId}
                  {...props}
                  item={item}
                  includeText={
                    item.variant === ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE
                  }
                  data-selector={`categorise-item-${index}`}
                />
              );
            }
          })}
        </BoxList>
      </SettingsGroup>

      <Box $gap={8} $marginTop={12}>
        <SecondaryButton
          type="button"
          $isSmall
          disabled={isAtMaxItems}
          onClick={handleAddImageOnly}
          data-selector="btn-add-image-only"
          data-testid="btn-add-image-only"
        >
          Add Image Only
        </SecondaryButton>
        <SecondaryButton
          type="button"
          $isSmall
          disabled={isAtMaxItems}
          onClick={handleAddTextOnly}
          data-selector="btn-add-text-only"
          data-testid="btn-add-text-only"
        >
          Add Text Only
        </SecondaryButton>
        <SecondaryButton
          type="button"
          $isSmall
          disabled={isAtMaxItems}
          onClick={handleAddImageAndText}
          data-selector="btn-add-image-and-text"
          data-testid="btn-add-image-and-text"
        >
          Add Image and Text
        </SecondaryButton>
      </Box>
    </>
  );
};

export default InteractiveResponseCategoriseItems;
